import React from "react";
import UsageIcon from "../../../../../images/membership/usage-grey.svg";
import PlusIcon from "../../../../../images/products/plus.svg";
import { Table } from "react-bootstrap";
import { observer } from "mobx-react";
import AddBenefitModal from "./AddBenefitModal";
import { PricingPageService } from "../services";
import {
  BenefitsModel,
  PriceBenefitsInputModel,
  PriceBenefitsValueMappingModel,
  ServiceAndMembershipOptionsPageModel,
} from "../models";
import {
  CollapsiblePanel,
  CustomLabel,
  FlexContainer,
  Input,
  IOption,
  MultiSelect,
  StyledTable,
  theme,
} from "@mindmemobile/mm-shared-global";
import { ProductOptionWrapper, UsageBodyWrapper } from "../styles";

type Props = {
  pricingPageData: PricingPageService;
  showAddBenefitModal: boolean;
  optionsPage: ServiceAndMembershipOptionsPageModel;
  updateOptionsPage: (data: ServiceAndMembershipOptionsPageModel) => void;
  toggleAddBenefitModal: () => void;
};
@observer
export default class UsageSection extends React.Component<Props> {
  textField(hasError: boolean, value: string, fieldName: keyof PriceBenefitsInputModel, index: number) {
      return (
          <Input
              value={value}
              error={hasError}
              type="text"
              onChange={(e) => this.updateValue(e.target.value, fieldName, index)}
              className="styled-input usage-input"
          />
      );
  }

  checkBox(value: boolean, label: string, className: string, fieldName: keyof PriceBenefitsInputModel, index = -1) {
      return (
          <Input
              value={value}
              type="checkbox"
              label={label}
              onChange={(e) => this.updateValue(e.target.checked, fieldName, index)}
              className={className}
          />
      );
  }

  updateValue(value: string | boolean, fieldName: keyof PriceBenefitsInputModel, index: number) {
      const clonedBenefits =
          this.props.optionsPage.benefits.map((b) => {
              return new PriceBenefitsInputModel({ ...b });
          }) || [];
      const updatedBenefits = clonedBenefits[index];
      switch (fieldName) {
          case 'ageLimit':
              updatedBenefits.ageLimit = value as boolean;
              break;
          case 'minAge':
              updatedBenefits.minAge = value as string;
              break;
          case 'maxValue':
              updatedBenefits.maxValue = value as string;
              break;
          case 'maxAge':
              updatedBenefits.maxAge = value as string;
              break;
      }

      if (index > -1 && updatedBenefits) {
          this.props.updateOptionsPage(
              new ServiceAndMembershipOptionsPageModel({
                  ...this.props.optionsPage,
                  benefits: clonedBenefits,
              }),
          );
      }
  }

  ageSection(benefit: PriceBenefitsInputModel, index: number) {
      const hasMinAgeError = this.hasError(benefit.errors, 'minAge');
      const hasMaxAgeError = this.hasError(benefit.errors, 'maxAge');
      return (
          <>
              {this.checkBox(benefit.ageLimit, 'Age Limits', 'age-limit-chkbox', 'ageLimit', index)}

              {benefit.ageLimit && (
                  <div>
                      <FlexContainer spacing={15} alignItems="center">
                          <CustomLabel color="#777">Age Range</CustomLabel>
                          {this.textField(hasMinAgeError, benefit.minAge, 'minAge', index)}
                          {this.textField(hasMaxAgeError, benefit.maxAge, 'maxAge', index)}
                      </FlexContainer>
                  </div>
              )}
          </>
      );
  }

  hasError(errors: any[], fieldName: string) {
      return errors.find((x) => x.field === fieldName) !== undefined;
  }

  updateSelectedBenefits(selectedBenefits: IOption[]) {
      this.props.updateOptionsPage(
          new ServiceAndMembershipOptionsPageModel({
              ...this.props.optionsPage,
              selectedBenefits: selectedBenefits.map(
                  (b) =>
                      new BenefitsModel({
                          id: b.value,
                          singularName:
                              this.props.pricingPageData.benefitsService.benefits.find((bo) => bo.id === b.value)
                                  ?.singularName || '',
                          pluralName:
                              this.props.pricingPageData.benefitsService.benefits.find((bo) => bo.id === b.value)
                                  ?.pluralName || '',
                      }),
              ),
          }),
      );
  }

  addNewBenefit(benefit: BenefitsModel) {
      if (!this.props.optionsPage.allowBenefits) return;
      const clonedSelectedBenefits =
          this.props.optionsPage.selectedBenefits?.map((b) => {
              return new BenefitsModel({ ...b });
          }) || [];

      const alreadySelected = clonedSelectedBenefits.find((b) => b.id === benefit.id);
      if (alreadySelected) return;

      clonedSelectedBenefits.push(benefit);
      this.props.updateOptionsPage(
          new ServiceAndMembershipOptionsPageModel({
              ...this.props.optionsPage,
              selectedBenefits: clonedSelectedBenefits,
          }),
      );
  }

  toggleBenefits() {
      this.props.updateOptionsPage(
          new ServiceAndMembershipOptionsPageModel({
              ...this.props.optionsPage,
              allowBenefits: this.props.optionsPage.allowBenefits === true ? false : true,
          }),
      );
  }

  get benefitOptions() {
      return (
          this.props.pricingPageData.benefitsService.benefits.map((benefit) => {
              return {
                  label: benefit.pluralName,
                  value: benefit.id,
              } as IOption;
          }) || []
      );
  }

  get benefitSection() {
      if (!this.props?.optionsPage.allowBenefits) return null;

      const hasError = this.hasError(this.props.optionsPage.errors || [], 'benefits');
      return (
          <>
              <div className="usage-content">
                  <MultiSelect
                      className={hasError ? 'error-border' : ''}
                      options={this.benefitOptions}
                      classNamePrefix={'usage'}
                      selectedOptions={this.selectedBenefits}
                      onChange={(newValues: IOption[]) => this.updateSelectedBenefits(newValues)}
                  />
                  <CustomLabel
                      fontSize="12px"
                      lineHeight="14px"
                      margin="15px 0px 0px "
                  >{`Select Benefits to award when item is purchased and payments are received. Manage in Settings > Payments.`}</CustomLabel>

                  {this.props.optionsPage?.benefits?.map((benefit, index) => {
                      const hasError = benefit.errors?.find((x) => x.field === 'maxValue') !== undefined;
                      return (
                          <CollapsiblePanel
                              key={index}
                              title={benefit.priceName}
                              inActive={!benefit.isActivePrice}
                              className="usage-header"
                              isShowStatus
                              collapsibleClassName="collapsed-card"
                          >
                              <FlexContainer spacing={50} alignItems="center">
                                  <div>
                                      <FlexContainer spacing={15} alignItems="center">
                                          <CustomLabel color="#777">Maximum Members</CustomLabel>
                                          {this.textField(hasError, benefit.maxValue, 'maxValue', index)}
                                      </FlexContainer>
                                  </div>
                                  {this.ageSection(benefit, index)}
                              </FlexContainer>
                              {this.benefitsMapping(benefit, index)}
                          </CollapsiblePanel>
                      );
                  })}
              </div>
          </>
      );
  }

  get selectedBenefits() {
      return (
          this.props?.optionsPage.selectedBenefits.map((benefit) => {
              return {
                  label: benefit.pluralName,
                  value: benefit.id,
              } as IOption;
          }) || []
      );
  }

  benefitsMapping(benefitInput: PriceBenefitsInputModel, index: number) {
      const benefits = this.props.optionsPage?.selectedBenefits || [];
      return (
          <StyledTable className="usage-table">
              <Table bsPrefix="styled-table" responsive>
                  <thead>
                      <th className="text-left">BILLING TYPE</th>
                      <th className="text-left">PAYMENT RECEIVED</th>
                      {benefits.map((benefit) => {
                          return <th key={benefit.id}>{benefit.pluralName}</th>;
                      })}
                      <th></th>
                  </thead>
                  <tbody>
                      {this.benefitMappingRow(benefitInput, index, true)}
                      {this.benefitMappingRow(benefitInput, index, false)}
                  </tbody>
              </Table>
          </StyledTable>
      );
  }

  benefitMappingRow(benefitInput: PriceBenefitsInputModel, benefitIndex: number, isTerms: boolean) {
      if (
          isTerms &&
          benefitInput.priceBillingType !== 'Term' &&
          benefitInput.priceBillingType !== 'TermPlusRecurring'
      )
          return null;

      if (
          !isTerms &&
          benefitInput.priceBillingType !== 'Recurring' &&
          benefitInput.priceBillingType !== 'TermPlusRecurring'
      )
          return null;

      const benefitMapping = isTerms ? benefitInput.termBenefits : benefitInput.recurringBenefits;
      return (
          <>
              <tr>
                  <td className="text-left">
                      <CustomLabel fontSize="14px">{isTerms ? 'TERM' : 'RECURRING'}</CustomLabel>
                  </td>
                  <td className="text-left">
                      <CustomLabel fontSize="14px" fontWeight="600">
                          $500.00 paid at purchase
                      </CustomLabel>
                  </td>
                  {benefitMapping.map((bm, idx) => {
                      const hasError = bm.errors?.find((e) => e.field === 'value') !== undefined;
                      return (
                          <td key={idx}>
                              <Input
                                  error={hasError}
                                  value={bm.value}
                                  type="text"
                                  onChange={(e) =>
                                      isTerms
                                          ? this.updateTermsBenefit(e.target.value, benefitIndex, idx)
                                          : this.updateRecurringBenefit(e.target.value, benefitIndex, idx)
                                  }
                              />
                          </td>
                      );
                  })}

                  <td></td>
              </tr>
          </>
      );
  }

  updateTermsBenefit(value: string, benefitIndex: number, benefitMappingIndex: number) {
      const clonedBenefits = this.clonedBenefits;
      const benefitToUpdate = clonedBenefits[benefitIndex];
      const clonedMappings =
          benefitToUpdate?.termBenefits.map((b) => {
              return new PriceBenefitsValueMappingModel({ ...b });
          }) || [];
      clonedMappings[benefitMappingIndex].value = value;
      benefitToUpdate.termBenefits = clonedMappings;
      this.props.updateOptionsPage(
          new ServiceAndMembershipOptionsPageModel({
              ...this.props.optionsPage,
              benefits: clonedBenefits,
          }),
      );
  }

  updateRecurringBenefit(value: string, benefitIndex: number, benefitMappingIndex: number) {
      const clonedBenefits = this.clonedBenefits;
      const benefitToUpdate = clonedBenefits[benefitIndex];
      const clonedMappings =
          benefitToUpdate?.recurringBenefits.map((b) => {
              return new PriceBenefitsValueMappingModel({ ...b });
          }) || [];
      clonedMappings[benefitMappingIndex].value = value;
      benefitToUpdate.recurringBenefits = clonedMappings;
      this.props.updateOptionsPage(
          new ServiceAndMembershipOptionsPageModel({
              ...this.props.optionsPage,
              benefits: clonedBenefits,
          }),
      );
  }

  get clonedBenefits(): PriceBenefitsInputModel[] {
      return (
          this.props.optionsPage.benefits.map((b) => {
              return new PriceBenefitsInputModel({ ...b });
          }) || []
      );
  }

  closeBeneiftModal(benefit?: BenefitsModel) {
      this.props.toggleAddBenefitModal();
      if (benefit) {
          this.addNewBenefit(benefit);
      }
  }

  render() {
      return (
          <ProductOptionWrapper>
              <CollapsiblePanel
                  title="Usage "
                  className="collapsed-labels donation-collapsed usage-section"
                  imgSrc={UsageIcon}
                  isShowInfoIcon
                  collapsibleClassName="collapsed-card"
              >
                  {this.props.showAddBenefitModal && (
                      <AddBenefitModal
                          benefitsService={this.props.pricingPageData.benefitsService}
                          onClose={(benefit) => this.closeBeneiftModal(benefit)}
                      />
                  )}
                  <UsageBodyWrapper>
                      <FlexContainer spacing={30} padding="0px 0px 25px">
                          <Input
                              value={this.props.optionsPage.allowBenefits === true}
                              type="checkbox"
                              label="Benefits"
                              onChange={() => this.toggleBenefits()}
                              className="usage-labels"
                          />
                          <div onClick={() => this.props.toggleAddBenefitModal()}>
                              <img src={PlusIcon} className="add-benefit"></img>
                              <CustomLabel color={theme.palette.info.dark}>New Benefit</CustomLabel>
                          </div>
                      </FlexContainer>
                      {this.benefitSection}
                  </UsageBodyWrapper>
              </CollapsiblePanel>
          </ProductOptionWrapper>
      );
  }
}
